import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { t } from "../lib/i18n";
import { qk } from "./keys";
import { invalidateProject } from "./projects";
import type {
  DecisionInput,
  DecisionPreview,
  MembershipRequest,
  RequestCreateInput,
  RequestStatus,
} from "./types";

export interface RequestFilters {
  mine?: boolean;
  status?: RequestStatus;
  project?: string;
}

export function useRequests(filters: RequestFilters, enabled = true) {
  return useQuery({
    queryKey: qk.requestList({ ...filters }),
    queryFn: ({ signal }) =>
      api<MembershipRequest[]>("/api/requests", {
        signal,
        query: { mine: filters.mine ? true : undefined, status: filters.status, project: filters.project },
      }),
    enabled,
  });
}

export function useCreateRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: RequestCreateInput) => api<MembershipRequest>("/api/requests", { body: input }),
    meta: { errorTitle: t("Request not sent") },
    onSuccess: (request) => {
      void client.invalidateQueries({ queryKey: qk.requests });
      invalidateProject(client, request.project);
    },
  });
}

export function useCancelRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<MembershipRequest>(`/api/requests/${id}/cancel`, { method: "POST" }),
    meta: { errorTitle: t("Request not cancelled") },
    onSuccess: (request) => {
      void client.invalidateQueries({ queryKey: qk.requests });
      invalidateProject(client, request.project);
    },
  });
}

/** The effects a decision would have, without writing anything (a POST, but side-effect free). */
export function useDecisionPreview(id: number | null, input: DecisionInput) {
  const params = {
    accept: input.accept,
    stream_id: input.stream_id ?? null,
    effective_date: input.effective_date || null,
  };
  return useQuery({
    queryKey: qk.requestPreview(id ?? 0, params),
    queryFn: ({ signal }) => api<DecisionPreview>(`/api/requests/${id}/preview`, { body: params, signal }),
    enabled: id !== null,
    staleTime: 0,
    retry: false,
  });
}

export function useDecideRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: DecisionInput & { id: number }) =>
      api<MembershipRequest>(`/api/requests/${id}/decision`, {
        body: {
          accept: input.accept,
          message: input.message ?? "",
          stream_id: input.stream_id ?? null,
          effective_date: input.effective_date || null,
        },
      }),
    meta: { errorTitle: t("Decision not saved") },
    onSuccess: (request) => {
      void client.invalidateQueries({ queryKey: qk.requests });
      void client.invalidateQueries({ queryKey: qk.admin });
      void client.invalidateQueries({ queryKey: qk.myAssignments });
      invalidateProject(client, request.project);
    },
  });
}
